/**
 * MonteCarloTab - Fortschrittliche Monte-Carlo-Analyse-Komponente für Portfolio-Strategien
 *
 * Diese hochentwickelte React-Komponente stellt das Herzstück der probabilistischen Portfolio-Analyse dar
 * und ermöglicht umfassende Vergleiche zwischen 1-Topf und 3-Topf Strategien basierend auf wissenschaftlichen
 * Monte-Carlo-Simulationen mit bis zu 10.000 Iterationen.
 *
 * Hauptfunktionen:
 * - Interaktive Visualisierung von Monte-Carlo-Simulationsergebnissen
 * - Statistische Performance-Vergleiche zwischen Portfolio-Strategien
 * - Dynamische Chart-Darstellung mit linearer Skalierung
 * - Detaillierte Risiko-Rendite-Metriken (Sharpe, Sortino, Drawdown)
 * - Erfolgsraten-Analyse und Perzentil-basierte Risikobewertung
 * - Professionelle UI basierend auf AWS Cloudscape Design System
 *
 * REFACTORED: Business Logic wurde in PerformanceCalculationService ausgelagert für bessere
 * Trennung von Darstellung und Berechnung sowie erhöhte Testbarkeit.
 *
 * @author Portfolio-3Topf Team
 * @version 2.0.0
 * @since 1.0.0
 */

import React, { useMemo } from 'react';
import {
  Alert,
  Badge,
  Box,
  ColumnLayout,
  Container,
  ExpandableSection,
  Header,
  Icon,
  LineChart,
  Popover,
  SpaceBetween,
  Table,
  Tabs,
  TextContent,
  Toggle,
} from '@cloudscape-design/components';
import {
  CombinedMonteCarloResult,
  CombinedMonteCarloStats,
} from '@/services/CombinedMonteCarloEngine';
import { PerformanceCalculationService } from '@/services/PerformanceCalculationService';
import { StatisticalHelpers } from '@/utils/statisticalHelpers';
import { OneTopfSimulationOptions, SteuerParameter } from '@/types';
import { ThreeTopfSimulationOptions as SimulationOptions } from '@/types/simulation';
import { GARCHRateCalculationIntegration, GARCHUtils } from '@/utils/garchModeling';
import { FailureAnalysisPanel } from './FailureAnalysisPanel';
import { ZinstopfOptimizationTab } from './ZinstopfOptimizationTab';
import { WithdrawalRateOptimizationTab } from './WithdrawalRateOptimizationTab';
import { CombinedOptimizationTab } from './CombinedOptimizationTab';
import { ScenarioMatrixTab } from './ScenarioMatrixTab';

/**
 * Props für die MonteCarloTab-Komponente
 *
 * Diese Schnittstelle definiert alle erforderlichen Daten und Callback-Funktionen
 * für die Monte-Carlo-Analyse-Darstellung.
 */
interface MonteCarloTabProps {
  /** Array von Monte-Carlo-Simulationsergebnissen mit 1-Topf und 3-Topf Vergleichsdaten */
  monteCarloResults: CombinedMonteCarloResult[];

  /** Aggregierte Statistiken der Monte-Carlo-Simulation (Erfolgsraten, Durchschnittswerte) */
  monteCarloStats: CombinedMonteCarloStats | null;

  /** Steuerparameter für Zugriff auf aktuelle Simulationseinstellungen (kann null sein beim Laden) */
  steuerParameter: SteuerParameter | null;

  /** Simulationsoptionen für 3-Topf-Strategie (zur GARCH-Erkennung) */
  simulationOptions?: SimulationOptions;

  /** MonteCarloEngine-Instanz für detaillierte Drill-Down-Analysen */
  monteCarloEngine?: import('@/services/CombinedMonteCarloEngine').CombinedMonteCarloEngine;

  /** Simulationsoptionen für 1-Topf-Strategie (zur GARCH-Erkennung) */
  oneTopfSimulationOptions?: OneTopfSimulationOptions;

  /** Krisendefinition für Simulationen */
  crisisDefinition: string;
}

/**
 * MonteCarloTab - Hauptkomponente für Monte-Carlo-Simulationsanalyse
 *
 * Diese Komponente rendert eine umfassende Analyse-Dashboard für Monte-Carlo-Simulationen
 * mit interaktiven Charts, statistischen Tabellen und Performance-Metriken.
 *
 * @param props - MonteCarloTabProps mit Simulationsdaten und Kontrollfunktionen
 * @returns JSX.Element - Vollständiges Monte-Carlo-Analyse-Dashboard
 *
 * @example
 * ```tsx
 * <MonteCarloTab
 *   monteCarloResults={simulationResults}
 *   monteCarloStats={aggregatedStats}
 *   steuerParameter={currentParameters}
 * />
 * ```
 *
 * Features:
 * - Interaktive Performance-Vergleichstabellen
 * - Dynamische Chart-Visualisierung mit linearer Skalierung
 * - Statistische Kennzahlen-Display (Sharpe Ratio, Drawdown, etc.)
 * - Responsive Layout basierend auf Cloudscape Design System
 */

/**
 * Helper function to render metric name with info tooltip
 * Renders the metric name with an info icon that shows detailed description on hover
 */
const renderMetricWithInfo = (metricName: string): React.ReactNode => {
  return (
    <SpaceBetween direction="horizontal" size="xs">
      <span>{metricName}</span>
      <Popover
        size="large"
        position="top"
        triggerType="custom"
        dismissButton={false}
        content={
          <Box variant="p">
            {PerformanceCalculationService.getMetricDescription(metricName)}
          </Box>
        }
      >
        <Icon name="status-info" size="small" />
      </Popover>
    </SpaceBetween>
  );
};

export const MonteCarloTab: React.FC<MonteCarloTabProps> = ({
  monteCarloResults,
  monteCarloStats,
  steuerParameter,
  simulationOptions,
  monteCarloEngine,
  oneTopfSimulationOptions,
  crisisDefinition,
}) => {
  // GARCH detection for both strategies
  const isThreeTopfUsingGARCH =
    simulationOptions?.investmentReturnOption &&
    GARCHUtils.isGARCHOption(simulationOptions.investmentReturnOption);
  const isOneTopfUsingGARCH =
    oneTopfSimulationOptions?.investmentReturnOption &&
    GARCHUtils.isGARCHOption(oneTopfSimulationOptions.investmentReturnOption);
  const isAnyStrategyUsingGARCH = isThreeTopfUsingGARCH || isOneTopfUsingGARCH;

  // State für Extreme Outcome Filter
  const [excludeExtremes, setExcludeExtremes] = React.useState(false);

  // Filter Monte Carlo results to exclude extreme 5% (best & worst)
  const filteredMonteCarloResults = useMemo(() => {
    if (!excludeExtremes || !monteCarloResults || monteCarloResults.length === 0) {
      return monteCarloResults;
    }

    // Sort by 3-Topf final balance to identify extremes
    const sorted = [...monteCarloResults].sort(
      (a, b) => a.threeTopf.finalBalance - b.threeTopf.finalBalance
    );

    // Calculate cutoff indices (exclude bottom 2.5% and top 2.5%)
    const bottomCutoff = Math.floor(sorted.length * 0.025);
    const topCutoff = Math.ceil(sorted.length * 0.975);

    // Get the middle 95% from sorted array
    const middleResults = sorted.slice(bottomCutoff, topCutoff);
    
    // Create a Set of iteration numbers to keep (for fast lookup)
    const iterationsToKeep = new Set(middleResults.map(r => r.iteration));
    
    // Filter original array to preserve original order
    const filtered = monteCarloResults.filter(r => iterationsToKeep.has(r.iteration));
    
    return filtered;
  }, [monteCarloResults, excludeExtremes]);

  // Calculate actual depletion counts (portfolios that depleted)
  // AND early failures (depletions BEFORE target age)
  // 
  // SUCCESS DEFINITION (verfeinert - 95%-Zieljahr-Schwelle):
  // - Portfolio ist ERFOLGREICH, wenn es das Zielalter erreicht UND im Zieljahr >= 95 %
  //   der Zielentnahme bedient (zentral: r.*.successfulStrategy aus der Engine).
  // - Portfolio ist FAILED, wenn es früh erschöpft ODER das Zieljahr unterfinanziert lässt.
  // - "Early" = Failure-Alter strikt vor dem Zielalter; ein "Zieljahr unterfinanziert"-Lauf
  //   zählt in total, aber nicht als early (Failure-Alter == Zielalter).
  const depletionCounts = useMemo(() => {
    if (!filteredMonteCarloResults || filteredMonteCarloResults.length === 0 || !steuerParameter) {
      return { 
        threeTopf: { total: 0, early: 0 }, 
        oneTopf: { total: 0, early: 0 } 
      };
    }
    
    const targetAge = steuerParameter.berechnungBisLebensalter;

    // Count depletions using actual simulated years (not corrupted depletionAge)
    let threeTopfTotal = 0;
    let threeTopfEarly = 0;
    let oneTopfTotal = 0;
    let oneTopfEarly = 0;
    
    filteredMonteCarloResults.forEach(r => {
      // 3-Topf: Failure == verfeinerte Erfolgs-Definition verletzt (Engine-Flag).
      // Failure-Alter = alter des letzten Eintrags (früh erschöpft: < Zielalter;
      // Zieljahr unterfinanziert: == Zielalter).
      const threeTopfDepleted = !r.threeTopf.successfulStrategy;

      if (threeTopfDepleted) {
        threeTopfTotal++;
        const threeTopfEntries = r.threeTopf.jahresergebnisse;
        const depletionAge = threeTopfEntries.length > 0
          ? threeTopfEntries[threeTopfEntries.length - 1].alter
          : steuerParameter.retirementAge;
        if (depletionAge < targetAge) {
          threeTopfEarly++;
        }
      }

      // 1-Topf: analog
      const oneTopfDepleted = !r.oneTopf.successfulStrategy;

      if (oneTopfDepleted) {
        oneTopfTotal++;
        const oneTopfEntries = r.oneTopf.jahresergebnisse;
        const depletionAge = oneTopfEntries.length > 0
          ? oneTopfEntries[oneTopfEntries.length - 1].alter
          : steuerParameter.retirementAge;
        if (depletionAge < targetAge) {
          oneTopfEarly++;
        }
      }
    });
    
    return {
      threeTopf: { total: threeTopfTotal, early: threeTopfEarly },
      oneTopf: { total: oneTopfTotal, early: oneTopfEarly },
    };
  }, [filteredMonteCarloResults, steuerParameter]);

  // Recalculate statistics with filtered results
  const displayMonteCarloStats = useMemo(() => {
    if (!excludeExtremes || !monteCarloEngine || !filteredMonteCarloResults || filteredMonteCarloResults.length === 0) {
      return monteCarloStats;
    }

    try {
      return monteCarloEngine.calculateCombinedStatistics(filteredMonteCarloResults);
    } catch (error) {
      console.error('Failed to calculate filtered statistics:', error);
      return monteCarloStats;
    }
  }, [filteredMonteCarloResults, excludeExtremes, monteCarloEngine, monteCarloStats]);

  // Calculate "both successful" rate: echte gemeinsame Zählung statt Math.min()
  const bothSuccessfulRate = useMemo(() => {
    if (!filteredMonteCarloResults || filteredMonteCarloResults.length === 0 || !steuerParameter) {
      return 0;
    }

    // Verfeinerte Erfolgs-Definition: beide Strategien müssen das Engine-Erfolgs-Flag
    // (Zielalter erreicht UND >= 95 % Zieljahr-Entnahme bedient) erfüllen.
    const bothSuccessfulCount = filteredMonteCarloResults.filter(
      r => r.threeTopf.successfulStrategy && r.oneTopf.successfulStrategy
    ).length;

    return filteredMonteCarloResults.length > 0
      ? (bothSuccessfulCount / filteredMonteCarloResults.length) * 100
      : 0;
  }, [filteredMonteCarloResults, steuerParameter]);

  // GARCH analysis data (cached for performance)
  const garchAnalysis = useMemo(() => {
    if (!isAnyStrategyUsingGARCH) {return null;}
    try {
      return GARCHRateCalculationIntegration.analyzeIndexDataForGARCH('world');
    } catch (error) {
      // Failed to load GARCH analysis
      return null;
    }
  }, [isAnyStrategyUsingGARCH]);

  // Helper function to calculate volatility distribution statistics
  const calculateVolatilityStats = (
    results: CombinedMonteCarloResult[],
    strategy: 'threeTopf' | 'oneTopf'
  ) => {
    if (!results || results.length === 0) {return null;}

    const returns: number[] = [];
    const volatilities: number[] = [];

    for (let i = 0; i < results.length; i++) {
      const portfolioData = strategy === 'threeTopf' ? results[i].threeTopf : results[i].oneTopf;
      const yearlyBalances = portfolioData.yearlyBalances;

      if (yearlyBalances.length < 2) {continue;}

      // Calculate year-over-year returns from balance changes
      const yearlyReturns: number[] = [];
      for (let j = 1; j < yearlyBalances.length; j++) {
        if (yearlyBalances[j - 1] > 0) {
          const returnRate =
            ((yearlyBalances[j] - yearlyBalances[j - 1]) / yearlyBalances[j - 1]) * 100;
          yearlyReturns.push(returnRate);
        }
      }

      returns.push(...yearlyReturns);

      // Calculate rolling volatility for this iteration (if we have enough data)
      if (yearlyReturns.length >= 3) {
        for (let j = 2; j < yearlyReturns.length; j++) {
          const windowReturns = yearlyReturns.slice(Math.max(0, j - 2), j + 1);
          const mean =
            windowReturns.reduce((sum: number, r: number) => sum + r, 0) / windowReturns.length;
          const variance =
            windowReturns.reduce((sum: number, r: number) => sum + Math.pow(r - mean, 2), 0) /
            windowReturns.length;
          volatilities.push(Math.sqrt(variance));
        }
      }
    }

    if (volatilities.length === 0 || returns.length === 0) {return null;}

    const sortedVols = volatilities.sort((a, b) => a - b);
    const highVolatilityCount = returns.filter(r => Math.abs(r) > 20).length;
    const leverageCount = returns.filter(r => r < -10).length;
    const clusteringPeriods = volatilities.filter(v => v > 20).length;

    return {
      meanVolatility:
        volatilities.reduce((sum: number, v: number) => sum + v, 0) / volatilities.length,
      p50Volatility: sortedVols[Math.floor(sortedVols.length * 0.5)],
      p90Volatility: sortedVols[Math.floor(sortedVols.length * 0.9)],
      p95Volatility: sortedVols[Math.floor(sortedVols.length * 0.95)],
      highVolatilityFrequency: (highVolatilityCount / returns.length) * 100,
      leverageEffectFrequency: (leverageCount / returns.length) * 100,
      volatilityClusteringPeriods: clusteringPeriods,
      volatilityClusteringFrequency: (clusteringPeriods / volatilities.length) * 100,
    };
  };

  // Calculate GARCH volatility statistics for each strategy
  const threeTopfVolatilityStats = useMemo(() => {
    return isThreeTopfUsingGARCH ? calculateVolatilityStats(filteredMonteCarloResults, 'threeTopf') : null;
  }, [filteredMonteCarloResults, isThreeTopfUsingGARCH]);

  const oneTopfVolatilityStats = useMemo(() => {
    return isOneTopfUsingGARCH ? calculateVolatilityStats(filteredMonteCarloResults, 'oneTopf') : null;
  }, [filteredMonteCarloResults, isOneTopfUsingGARCH]);

  // Chart data generator for portfolio distributions
  const chartData = useMemo(() => {
    if (!filteredMonteCarloResults || filteredMonteCarloResults.length === 0) {
      return [];
    }

    // Helper function to create distribution data for charts
    const createDistribution = (balances: number[], label: string) => ({
      title: label,
      type: 'line' as const,
      data: balances.map((balance, index) => ({
        x: index,
        y: Math.round(balance),

        ariaLabel: `${label}: ${Math.round(balance)
          .toString()
          .replace(/\B(?=(\d{3})+(?!\d))/g, '.')} €`,
      })),
      color: label.includes('3-Topf') ? '#0073bb' : '#00a86b',
    });

    const threeTopfBalances = filteredMonteCarloResults.map(r => r.threeTopf.finalBalance);

    const oneTopfBalances = filteredMonteCarloResults.map(r => r.oneTopf.finalBalance);

    return [
      createDistribution(threeTopfBalances, '3-Topf Verteilung'),

      createDistribution(oneTopfBalances, '1-Topf Verteilung'),
    ];
  }, [filteredMonteCarloResults]);

  // Performance data using the extracted service
  const threeTopfPerformanceData = useMemo(() => {
    if (!filteredMonteCarloResults) {return [];}
    return PerformanceCalculationService.generatePerformanceSummary(
      filteredMonteCarloResults,
      'threeTopf',
      steuerParameter?.vorhandenesKapital ?? 0
    );
  }, [filteredMonteCarloResults, steuerParameter]);

  const oneTopfPerformanceData = useMemo(() => {
    if (!filteredMonteCarloResults) {return [];}
    return PerformanceCalculationService.generatePerformanceSummary(
      filteredMonteCarloResults,
      'oneTopf',
      steuerParameter?.vorhandenesKapital ?? 0
    );
  }, [filteredMonteCarloResults, steuerParameter]);

  // Portfolio Survival Chart Data
  const survivalChartData = useMemo(() => {
    if (!filteredMonteCarloResults || filteredMonteCarloResults.length === 0) {
      return [];
    }

    // Calculate the full simulation period (retirement age to berechnungBisLebensalter)
    const startAge = steuerParameter ? steuerParameter.retirementAge : 65;
    const endAge = steuerParameter ? steuerParameter.berechnungBisLebensalter : 100;
    const maxYears = endAge - startAge;

    // Generate survival data for each year
    const survivalData: { [key: string]: { x: number; y: number }[] } = {
      threeTopf: [],

      oneTopf: [],
    };

    for (let year = 0; year <= maxYears; year++) {
      // Calculate survival rate: % of portfolios that survived at least this many years
      // Eintrag i enthält Daten für Jahr i → "Lebt bei Jahr i" bedeutet length >= i+1
      const threeTopfSurvivalRate =
        (filteredMonteCarloResults.filter(r => r.threeTopf.jahresergebnisse.length >= year + 1).length /
          filteredMonteCarloResults.length) *
        100;

      const oneTopfSurvivalRate =
        (filteredMonteCarloResults.filter(r => r.oneTopf.jahresergebnisse.length >= year + 1).length /
          filteredMonteCarloResults.length) *
        100;

      // Convert retirement year to actual age
      // year=0 represents retirement age, year=maxYears represents final age
      const age = startAge + year;

      survivalData.threeTopf.push({
        x: age,
        y: Math.round(threeTopfSurvivalRate),
      });

      survivalData.oneTopf.push({
        x: age,
        y: Math.round(oneTopfSurvivalRate),
      });
    }

    return [
      {
        title: '3-Topf Portfolio',
        type: 'line' as const,
        data: survivalData.threeTopf,
        color: '#0073bb',
      },

      {
        title: '1-Topf Portfolio',
        type: 'line' as const,
        data: survivalData.oneTopf,
        color: '#00a86b',
      },
    ];
  }, [filteredMonteCarloResults, steuerParameter]);

  // Portfolio Balance Chart Data for each strategy - FIXED to use actual yearly balances
  const createPortfolioBalanceChartData = (strategy: 'threeTopf' | 'oneTopf') => {
    if (!filteredMonteCarloResults || filteredMonteCarloResults.length === 0) {
      return [];
    }

    // Find the maximum years across all iterations for this strategy
    // yearlyBalances includes initial year (index 0) + all simulation years
    // We want to process all years including the final year
    const maxYears = Math.max(...filteredMonteCarloResults.map(r => r[strategy].yearlyBalances.length - 1));

    // Create percentile data for each year
    const percentileData: { [key: string]: { x: number; y: number }[] } = {
      p10: [],
      p25: [],
      p50: [],
      p75: [],
      p90: [],
    };

    // For each year, calculate portfolio balance percentiles using ACTUAL simulation data
    // Start from year 0 to include the retirement starting age, go to maxYears inclusive
    for (let year = 0; year <= maxYears; year++) {
      // Get actual portfolio balances at this year from all iterations
      const balancesAtYear = filteredMonteCarloResults
        .map(r => {
          const yearlyBalances = r[strategy].yearlyBalances;
          // Use actual simulation data if available, otherwise 0 if portfolio depleted
          // year starts from 0, so we access yearlyBalances[year] directly
          return year < yearlyBalances.length ? yearlyBalances[year] : 0;
        })
        .filter(balance => balance >= 0); // Remove any invalid data

      if (balancesAtYear.length === 0) {
        // No data for this year
        continue;
      }

      // Calculate percentiles from actual simulation data using linear
      // interpolation (StatisticalHelpers.calculatePercentile), consistent
      // with all other percentile figures shown in this app (e.g. the
      // statistics tables). Nearest-rank indexing (sorted[floor(p*n)]) was
      // used previously, which is badly biased for small n (e.g. n=10 makes
      // "P90" equal to the maximum).
      const p10 = StatisticalHelpers.calculatePercentile(balancesAtYear, 10);
      const p25 = StatisticalHelpers.calculatePercentile(balancesAtYear, 25);
      const p50 = StatisticalHelpers.calculatePercentile(balancesAtYear, 50);
      const p75 = StatisticalHelpers.calculatePercentile(balancesAtYear, 75);
      const p90 = StatisticalHelpers.calculatePercentile(balancesAtYear, 90);

      // Convert retirement year to actual age
      // year=0 represents retirement age, year=maxYears represents final age
      const age = (steuerParameter ? steuerParameter.retirementAge : 65) + year;

      percentileData.p10.push({
        x: age,
        y: Math.round(p10),
      });
      percentileData.p25.push({
        x: age,
        y: Math.round(p25),
      });
      percentileData.p50.push({
        x: age,
        y: Math.round(p50),
      });
      percentileData.p75.push({
        x: age,
        y: Math.round(p75),
      });
      percentileData.p90.push({
        x: age,
        y: Math.round(p90),
      });
    }

    return [
      {
        title: '10th Percentile',
        type: 'line' as const,
        data: percentileData.p10,
        color: '#EF4444', // Red - worst case
      },
      {
        title: '25th Percentile',
        type: 'line' as const,
        data: percentileData.p25,
        color: '#F59E0B', // Orange - below average
      },
      {
        title: '50th Percentile (Median)',
        type: 'line' as const,
        data: percentileData.p50,
        color: '#3B82F6', // Blue - median case
      },
      {
        title: '75th Percentile',
        type: 'line' as const,
        data: percentileData.p75,
        color: '#10B981', // Green - above average
      },
      {
        title: '90th Percentile',
        type: 'line' as const,
        data: percentileData.p90,
        color: '#059669', // Dark green - best case
      },
    ];
  };

  // Portfolio balance chart data for each strategy
  const threeTopfBalanceChartData = useMemo(
    () => createPortfolioBalanceChartData('threeTopf'),
    [filteredMonteCarloResults, steuerParameter]
  );

  const oneTopfBalanceChartData = useMemo(
    () => createPortfolioBalanceChartData('oneTopf'),
    [filteredMonteCarloResults, steuerParameter]
  );

  // Kapitalindex Chart Data for each strategy
  const createKapitalIndexChartData = (strategy: 'threeTopf' | 'oneTopf') => {
    if (!filteredMonteCarloResults || filteredMonteCarloResults.length === 0) {
      return [];
    }

    // Find the maximum years across all iterations for this strategy
    const maxYears = Math.max(...filteredMonteCarloResults.map(r => r[strategy].kapitalIndex.length - 1));

    // Create percentile data for each year
    const percentileData: { [key: string]: { x: number; y: number }[] } = {
      p10: [],
      p25: [],
      p50: [],
      p75: [],
      p90: [],
    };

    // For each year, calculate Kapitalindex percentiles using ACTUAL simulation data
    for (let year = 0; year <= maxYears; year++) {
      // Get actual Kapitalindex values at this year from all iterations
      const kapitalIndexAtYear = filteredMonteCarloResults
        .map(r => {
          const kapitalIndex = r[strategy].kapitalIndex;
          // Use actual simulation data if available; exclude if no data (depleted)
          // year starts from 0, so we access kapitalIndex[year] directly
          return year < kapitalIndex.length ? kapitalIndex[year] : null;
        })
        .filter((index): index is number => index !== null && index >= 0); // Remove null/invalid data

      if (kapitalIndexAtYear.length === 0) {
        // No data for this year
        continue;
      }

      // Calculate percentiles from actual simulation data using linear
      // interpolation (StatisticalHelpers.calculatePercentile), consistent
      // with all other percentile figures shown in this app. Nearest-rank
      // indexing (sorted[floor(p*n)]) was used previously, which is badly
      // biased for small n (e.g. n=10 makes "P90" equal to the maximum), and
      // the `|| 100` fallback wrongly turned a legitimate index value of 0
      // into 100.
      // Survivorship note: kapitalIndexAtYear already excludes depleted
      // iterations (their series simply ends), so percentiles for later
      // years are computed only over the runs that are still "alive" at
      // that year - not a bug, but a data-shape caveat (see chart
      // description below). Stored indices are also rounded integers (see
      // CombinedMonteCarloEngine), so sub-point differences are invisible.
      const p10 = StatisticalHelpers.calculatePercentile(kapitalIndexAtYear, 10);
      const p25 = StatisticalHelpers.calculatePercentile(kapitalIndexAtYear, 25);
      const p50 = StatisticalHelpers.calculatePercentile(kapitalIndexAtYear, 50);
      const p75 = StatisticalHelpers.calculatePercentile(kapitalIndexAtYear, 75);
      const p90 = StatisticalHelpers.calculatePercentile(kapitalIndexAtYear, 90);

      // Convert retirement year to actual age
      // year=0 represents retirement age, year=maxYears represents final age
      const age = (steuerParameter ? steuerParameter.retirementAge : 65) + year;

      percentileData.p10.push({
        x: age,
        y: Math.round(p10),
      });
      percentileData.p25.push({
        x: age,
        y: Math.round(p25),
      });
      percentileData.p50.push({
        x: age,
        y: Math.round(p50),
      });
      percentileData.p75.push({
        x: age,
        y: Math.round(p75),
      });
      percentileData.p90.push({
        x: age,
        y: Math.round(p90),
      });
    }

    return [
      {
        title: '10. Perzentil',
        type: 'line' as const,
        data: percentileData.p10,
        valueFormatter: (value: number) => `${value}`,
        color: '#FF6B6B',
      },
      {
        title: '25. Perzentil',
        type: 'line' as const,
        data: percentileData.p25,
        valueFormatter: (value: number) => `${value}`,
        color: '#FFA500',
      },
      {
        title: 'Median (50%)',
        type: 'line' as const,
        data: percentileData.p50,
        valueFormatter: (value: number) => `${value}`,
        color: '#32CD32',
      },
      {
        title: '75. Perzentil',
        type: 'line' as const,
        data: percentileData.p75,
        valueFormatter: (value: number) => `${value}`,
        color: '#1E90FF',
      },
      {
        title: '90. Perzentil',
        type: 'line' as const,
        data: percentileData.p90,
        valueFormatter: (value: number) => `${value}`,
        color: '#8A2BE2',
      },
    ];
  };

  const threeTopfKapitalIndexChartData = useMemo(
    () => createKapitalIndexChartData('threeTopf'),
    [filteredMonteCarloResults, steuerParameter]
  );
  const oneTopfKapitalIndexChartData = useMemo(
    () => createKapitalIndexChartData('oneTopf'),
    [filteredMonteCarloResults, steuerParameter]
  );


  if (!monteCarloResults || monteCarloResults.length === 0) {
    return (
      <Box textAlign="left" color="text-body-secondary">
        <p>Klicken Sie auf "Monte-Carlo-Simulation starten", um die Ergebnisse hier anzuzeigen.</p>
      </Box>
    );
  }

  return (
    <>
    <Tabs
      tabs={[
        {
          label: 'Standard-Analyse',
          id: 'standard',
          content: (
            <SpaceBetween direction="vertical" size="l">
      {/* Monte Carlo Analysis Filter */}
      <Container
        header={
          <Header variant="h3">
            Monte-Carlo-Analyse Einstellungen
          </Header>
        }
      >
        <Toggle
          checked={excludeExtremes}
          onChange={({ detail }) => setExcludeExtremes(detail.checked)}
        >
          <Box fontSize="body-m">
            <strong>Extreme 2,5% ausschließen (beste & schlechteste)</strong>
          </Box>
        </Toggle>
        {excludeExtremes && (
          <Box color="text-status-info" fontSize="body-s" margin={{ top: 'xs' }}>
            <Badge color="blue">Filter aktiv</Badge>
            {' '}Zeige {filteredMonteCarloResults.length.toLocaleString('de-DE')} Iterationen
              (ausgeschlossen: {Math.floor(monteCarloResults.length * 0.025)} schlechteste + 
              {Math.ceil(monteCarloResults.length * 0.025)} beste = {monteCarloResults.length - filteredMonteCarloResults.length} extreme Szenarien)
          </Box>
        )}
      </Container>

      {/* Monte Carlo Simulation Summary */}
      {steuerParameter && displayMonteCarloStats && (
        <Container>
          <Header variant="h3">
            Monte-Carlo-Simulation-Zusammenfassung
          </Header>
          <Box>
            <TextContent>
                <div style={{ lineHeight: '1.6', textAlign: 'justify' }}>
                  <div style={{ marginBottom: '4px' }}>
                    <strong>Simulationsparameter:</strong> Zeitraum:{' '}
                  {steuerParameter.berechnungBisLebensalter - steuerParameter.retirementAge} Jahre
                  (Alter {steuerParameter.retirementAge}-{steuerParameter.berechnungBisLebensalter}
                  ), Startkapital: {steuerParameter.vorhandenesKapital.toLocaleString('de-DE')} €,
                  Jährliche Entnahme:{' '}
                  {(
                    (steuerParameter.vorhandenesKapital * steuerParameter.portfolioEntnahmeRate) /
                    100
                  ).toLocaleString('de-DE')}{' '}
                  € ({steuerParameter.portfolioEntnahmeRate}%), Inflationsmodell:{' '}
                  {(() => {
                    switch (simulationOptions?.inflationOption) {
                      case 'keine-inflation':
                        return 'Keine Inflation (0%)';
                      case 'parameter-inflation':
                        return `Inflation mit Prozentwert aus Eingabeparameter (${steuerParameter?.inflationsrate || 2.0}%)`;
                      case 'historische-inflation':
                        return 'Inflation als Zufallswert aus historische Inflation';
                      case 'statistisch-inflation':
                        return 'Inflation als Zufallswert basierend auf Mittelwert und Standardabweichung';
                      default:
                        return 'Keine Inflation (0%)';
                    }
                  })()}
                  , Renditemodell:{' '}
                  {(() => {
                    switch (simulationOptions?.investmentReturnOption) {
                      case 'keine-rendite':
                        return 'Keine Rendite (0%)';
                      case 'parameter-rendite':
                        return `Normalverteilte Rendite um Parameterwert (Mittelwert: ${steuerParameter?.renditeInvestitionstopf || 7.0}%, Volatilität: ~16%)`;
                      case 'konstante-rendite':
                        return `Konstante Rendite aus Parameterwert (${steuerParameter?.renditeInvestitionstopf || 7.0}%)`;
                      case 'historische-rendite':
                        return 'Rendite als Zufallswert aus MSCI World historischen Renditen';
                      case 'statistisch-rendite':
                        return 'Rendite als Zufallswert basierend auf Mittelwert und Standardabweichung der historischen Renditen des MSCI World';
                      case 'historische-garch':
                        return 'Erweiterte historische Renditen mit GARCH-Volatilitätsmodellierung (Krisen-Cluster, Leverage-Effekte)';
                      case 'statistisch-garch':
                        return 'Statistische Renditeverteilung mit zeitvariabler GARCH-Volatilität (Mean Reversion, Fat Tails)';
                      default:
                        return `Normalverteilte Rendite um Parameterwert (Mittelwert: ${steuerParameter?.renditeInvestitionstopf || 7.0}%, Volatilität: ~16%)`;
                    }
                  })()}
                  , Tagesgeldmodell:{' '}
                  {(() => {
                    switch (simulationOptions?.tagesgeldOption) {
                      case 'keine-rendite':
                        return 'Keine Rendite (0%)';
                      case 'parameter-rendite':
                        return `Rendite aus Parameterwert (${steuerParameter?.zinssatzVerbrauchstopf || 1.5}%)`;
                      case 'historisch-tagesgeld':
                        return 'Rendite als Zufallswert aus historischen Tagesgeld-Zinssätzen';
                      case 'statistisch-tagesgeld':
                        return 'Rendite als Zufallswert basierend auf Mittelwert und Standardabweichung der historischen Tagesgeld-Zinssätze';
                      default:
                        return `Rendite aus Parameterwert (${steuerParameter?.zinssatzVerbrauchstopf || 1.5}%)`;
                    }
                  })()}
                  , Zinstopfmodell:{' '}
                  {(() => {
                    switch (simulationOptions?.zinstopfOption) {
                      case 'keine-rendite':
                        return 'Keine Rendite (0%)';
                      case 'parameter-rendite':
                        return `Rendite aus Parameterwert (${steuerParameter?.zinssatzZinstopf || 3.0}%)`;
                      case 'historisch-zinstopf':
                        return 'Rendite als Zufallswert aus historischen Zinstopf-Performances';
                      case 'statistisch-zinstopf':
                        return 'Rendite als Zufallswert basierend auf Mittelwert und Standardabweichung der historischen Zinstopf-Performances';
                      default:
                        return `Rendite aus Parameterwert (${steuerParameter?.zinssatzZinstopf || 3.0}%)`;
                    }
                  })()}
                  , Krisendefinition:{' '}
                  {(() => {
                    switch (crisisDefinition) {
                      case 'negative-return':
                        return 'Krise bei negativer Jahresrendite';
                      case 'kapitalindex-drawdown':
                        return 'Krise bei Kapitalindex-Rückgang - Ende erst bei Erholung';
                      default:
                        return 'Krise bei negativer Jahresrendite';
                    }
                  })()}
                </div>

                <div style={{ marginBottom: '4px' }}>
                  <strong>Ergebnisse 3-Topf:</strong> Endkapital{' '}
                  {Math.round(displayMonteCarloStats.threeTopf.averageFinalBalance).toLocaleString(
                    'de-DE'
                  )}{' '}
                  €, Durchschnittliche Laufzeit{' '}
                  {displayMonteCarloStats.threeTopf.averageYearsFunded.toFixed(2)} Jahre, Erfolgsquote{' '}
                  {displayMonteCarloStats.threeTopf.successRate.toFixed(1)}%
                </div>

                <div style={{ marginBottom: '4px' }}>
                  <strong>Ergebnisse 1-Topf:</strong> Endkapital{' '}
                  {Math.round(displayMonteCarloStats.oneTopf.averageFinalBalance).toLocaleString('de-DE')}{' '}
                  €, Durchschnittliche Laufzeit{' '}
                  {displayMonteCarloStats.oneTopf.averageYearsFunded.toFixed(2)} Jahre, Erfolgsquote{' '}
                  {displayMonteCarloStats.oneTopf.successRate.toFixed(1)}%
                </div>

                <div style={{ marginBottom: '4px' }}>
                  <strong>Vergleich:</strong>{' '}
                  {displayMonteCarloStats.oneTopf.averageFinalBalance >
                  displayMonteCarloStats.threeTopf.averageFinalBalance
                    ? `1-Topf überlegen (+${Math.round(displayMonteCarloStats.oneTopf.averageFinalBalance - displayMonteCarloStats.threeTopf.averageFinalBalance).toLocaleString('de-DE')} €)`
                    : `3-Topf überlegen (+${Math.round(displayMonteCarloStats.threeTopf.averageFinalBalance - displayMonteCarloStats.oneTopf.averageFinalBalance).toLocaleString('de-DE')} €)`}
                  . Beide Strategien erfolgreich in{' '}
                  {bothSuccessfulRate.toFixed(1)}
                  % der Fälle.
                </div>

                <div style={{ marginBottom: '4px' }}>
                  <strong>Fazit:</strong>{' '}
                  {displayMonteCarloStats.oneTopf.successRate > displayMonteCarloStats.threeTopf.successRate
                    ? '1-Topf-Strategie'
                    : '3-Topf-Strategie'}{' '}
                  robuster in diesem Szenario. Empfehlung:{' '}
                  {displayMonteCarloStats.oneTopf.successRate > displayMonteCarloStats.threeTopf.successRate
                    ? '1-Topf'
                    : '3-Topf'}{' '}
                  bei aktuellen Marktbedingungen. Individuelle Risikotoleranz berücksichtigen.
                </div>
              </div>
            </TextContent>
          </Box>
        </Container>
      )}

      {/* Summary Comparison */}
      {monteCarloStats && (
        <Container header={<Header variant="h3">Strategievergleich - Übersicht</Header>}>
          <SpaceBetween direction="vertical" size="m">
            <ColumnLayout columns={6} borders="horizontal">
              <Box>
                <Box variant="awsui-key-label">Iterationen</Box>
                <Box variant="awsui-value-large">
                  {filteredMonteCarloResults.length.toLocaleString('de-DE')}
                </Box>
              </Box>
              <Box>
                <Box variant="awsui-key-label">3-Topf überlegen</Box>
                <Box
                  variant="awsui-value-large"
                  color={
                    (displayMonteCarloStats?.comparison?.threeTopfAdvantageRate ?? 0) >= 70
                      ? 'text-status-success'
                      : (displayMonteCarloStats?.comparison?.threeTopfAdvantageRate ?? 0) >= 50
                        ? 'text-status-warning'
                        : 'text-status-error'
                  }
                >
                  {displayMonteCarloStats?.comparison?.threeTopfAdvantageRate?.toFixed(1) || '0.0'}%
                </Box>
                <Box variant="small" color="text-body-secondary">
                  der Iterationen
                </Box>
              </Box>
              <Box>
                <Box variant="awsui-key-label">1-Topf überlegen</Box>
                <Box
                  variant="awsui-value-large"
                  color={
                    (displayMonteCarloStats?.comparison?.oneTopfAdvantageRate ?? 0) >= 70
                      ? 'text-status-success'
                      : (displayMonteCarloStats?.comparison?.oneTopfAdvantageRate ?? 0) >= 50
                        ? 'text-status-warning'
                        : 'text-status-error'
                  }
                >
                  {displayMonteCarloStats?.comparison?.oneTopfAdvantageRate?.toFixed(1) || '0.0'}%
                </Box>
                <Box variant="small" color="text-body-secondary">
                  der Iterationen
                </Box>
              </Box>
              <Box>
                <Box variant="awsui-key-label">3-Topf Erfolgsquote</Box>
                <Box
                  variant="awsui-value-large"
                  color={
                    (displayMonteCarloStats?.threeTopf?.successRate ?? 0) >= 90
                      ? 'text-status-success'
                      : (displayMonteCarloStats?.threeTopf?.successRate ?? 0) >= 70
                        ? 'text-status-warning'
                        : 'text-status-error'
                  }
                >
                  {displayMonteCarloStats?.threeTopf?.successRate?.toFixed(1) || '0.0'}%
                </Box>
              </Box>

              <Box>
                <Box variant="awsui-key-label">1-Topf Erfolgsquote</Box>
                <Box
                  variant="awsui-value-large"
                  color={
                    (displayMonteCarloStats?.oneTopf?.successRate ?? 0) >= 90
                      ? 'text-status-success'
                      : (displayMonteCarloStats?.oneTopf?.successRate ?? 0) >= 70
                        ? 'text-status-warning'
                        : 'text-status-error'
                  }
                >
                  {displayMonteCarloStats?.oneTopf?.successRate?.toFixed(1) || '0.0'}%
                </Box>
              </Box>
            </ColumnLayout>
          </SpaceBetween>
        </Container>
      )}

      {/* Performance Analysis Summary */}
      <Container header={<Header variant="h3">Performance-Analyse Zusammenfassung</Header>}>
        <SpaceBetween direction="vertical" size="m">
          <ColumnLayout columns={2}>
            <Box>
              <Header variant="h3">3-Topf Strategie Highlights</Header>
              <SpaceBetween direction="vertical" size="xs">
                <Box>
                  <Box variant="awsui-key-label">Erfolgsquote</Box>
                  <Box
                    variant="awsui-value-large"
                    color={
                      (displayMonteCarloStats?.threeTopf?.successRate ?? 0) >= 90
                        ? 'text-status-success'
                        : (displayMonteCarloStats?.threeTopf?.successRate ?? 0) >= 70
                          ? 'text-status-warning'
                          : 'text-status-error'
                    }
                  >
                    {displayMonteCarloStats?.threeTopf?.successRate?.toFixed(1) || '0.0'}%
                  </Box>
                </Box>
                <Box>
                  <Box variant="awsui-key-label">Durchschnittlicher Endsaldo</Box>
                  <Box variant="awsui-value-large">
                    {Math.round(
                      displayMonteCarloStats?.threeTopf?.averageFinalBalance || 0
                    ).toLocaleString('de-DE')}{' '}
                    €
                  </Box>
                </Box>
                <Box>
                  <Box variant="awsui-key-label">Median Endsaldo</Box>
                  <Box variant="awsui-value-large">
                    {Math.round(
                      displayMonteCarloStats?.threeTopf?.medianFinalBalance || 0
                    ).toLocaleString('de-DE')}{' '}
                    €
                  </Box>
                </Box>
                <Box>
                  <Box variant="awsui-key-label">Durchschnittliche Jahre finanziert</Box>
                  <Box variant="awsui-value-large">
                    {(displayMonteCarloStats?.threeTopf?.averageYearsFunded || 0).toFixed(1)} Jahre
                  </Box>
                </Box>
              </SpaceBetween>
            </Box>

            <Box>
              <Header variant="h3">1-Topf Strategie Highlights</Header>
              <SpaceBetween direction="vertical" size="xs">
                <Box>
                  <Box variant="awsui-key-label">Erfolgsquote</Box>
                  <Box
                    variant="awsui-value-large"
                    color={
                      (displayMonteCarloStats?.oneTopf?.successRate ?? 0) >= 90
                        ? 'text-status-success'
                        : (displayMonteCarloStats?.oneTopf?.successRate ?? 0) >= 70
                          ? 'text-status-warning'
                          : 'text-status-error'
                    }
                  >
                    {displayMonteCarloStats?.oneTopf?.successRate?.toFixed(1) || '0.0'}%
                  </Box>
                </Box>
                <Box>
                  <Box variant="awsui-key-label">Durchschnittlicher Endsaldo</Box>
                  <Box variant="awsui-value-large">
                    {Math.round(displayMonteCarloStats?.oneTopf?.averageFinalBalance || 0).toLocaleString(
                      'de-DE'
                    )}{' '}
                    €
                  </Box>
                </Box>
                <Box>
                  <Box variant="awsui-key-label">Median Endsaldo</Box>
                  <Box variant="awsui-value-large">
                    {Math.round(displayMonteCarloStats?.oneTopf?.medianFinalBalance || 0).toLocaleString(
                      'de-DE'
                    )}{' '}
                    €
                  </Box>
                </Box>
                <Box>
                  <Box variant="awsui-key-label">Durchschnittliche Jahre finanziert</Box>
                  <Box variant="awsui-value-large">
                    {(displayMonteCarloStats?.oneTopf?.averageYearsFunded || 0).toFixed(1)} Jahre
                  </Box>
                </Box>
              </SpaceBetween>
            </Box>
          </ColumnLayout>
        </SpaceBetween>
      </Container>


      {/* GARCH Information Panel (when GARCH is being used) */}
      {isAnyStrategyUsingGARCH && garchAnalysis && (
        <ExpandableSection headerText="GARCH-Volatilitätsmodellierung im Monte Carlo">
          <SpaceBetween direction="vertical" size="l">
            <Alert type="info" statusIconAriaLabel="GARCH Info">
              <strong>Erweiterte Volatilitätsmodellierung aktiv:</strong> Diese Monte Carlo Simulation verwendet EGARCH(1,1) Modelle mit True Maximum Likelihood Estimation für realistische Volatilitätsmuster, Krisen-Clustering und Leverage-Effekte. Die v2.4.2 Implementierung bietet verbesserte Parameterschätzung und Stabilitätskorrekturen.
            </Alert>

            {/* Model Overview Section */}
            <Container header={<Header variant="h3">Modell-Übersicht</Header>}>
              <ColumnLayout columns={3} borders="horizontal">
                <Box>
                  <Box variant="awsui-key-label">GARCH-Modell</Box>
                  <Box variant="awsui-value-large">EGARCH(1,1)</Box>
                  <Box variant="small" color="text-body-secondary">
                    Exponentieller GARCH mit asymmetrischen Effekten
                  </Box>
                </Box>
                <Box>
                  <Box variant="awsui-key-label">Datenbasis</Box>
                  <Box variant="awsui-value-large">{garchAnalysis.dataPoints}</Box>
                  <Box variant="small" color="text-body-secondary">
                    Historische Datenpunkte (MSCI World)
                  </Box>
                </Box>
                <Box>
                  <Box variant="awsui-key-label">Anwendungsbereich</Box>
                  <Box variant="awsui-value-large">Investitionstopf</Box>
                  <Box variant="small" color="text-body-secondary">
                    Aktienrenditen mit Volatilitäts-Clustering
                  </Box>
                </Box>
              </ColumnLayout>
            </Container>

            {/* Mathematical Specification */}
            <Container header={<Header variant="h3">Mathematische Spezifikation</Header>}>
              <SpaceBetween direction="vertical" size="s">
                <Box>
                  <Box variant="awsui-key-label">EGARCH(1,1) Formel nach Nelson (1991)</Box>
                  <TextContent>
                    <p><strong>log(σₜ²) = ω + β·log(σₜ₋₁²) + α·g(εₜ₋₁)</strong></p>
                    <p>wobei: <strong>g(ε) = θ·ε + γ·(|ε| - E[|ε|])</strong></p>
                    <p>und: <strong>εₜ = (rₜ - μ) / σₜ</strong> (standardisierte Residuen)</p>
                  </TextContent>
                </Box>
                <ColumnLayout columns={2} borders="horizontal">
                  <Box>
                    <Box variant="awsui-key-label">Innovations-Verteilung</Box>
                    <Box variant="awsui-value-large">
                      {parseFloat(garchAnalysis.kurtosis) > 5 ? 'Student-t' : 'Normal'}
                    </Box>
                    <Box variant="small" color="text-body-secondary">
                      {parseFloat(garchAnalysis.kurtosis) > 5 
                        ? `Heavy Tails (Kurtosis: ${garchAnalysis.kurtosis})`
                        : `Standard Normal (Kurtosis: ${garchAnalysis.kurtosis})`
                      }
                    </Box>
                  </Box>
                  <Box>
                    <Box variant="awsui-key-label">E[|ε|] Korrektur</Box>
                    <Box variant="awsui-value-large">
                      {parseFloat(garchAnalysis.kurtosis) > 5 ? '√(2(ν-2)/πν)' : '√(2/π)'}
                    </Box>
                    <Box variant="small" color="text-body-secondary">
                      Erwartungswert der absoluten standardisierten Residuen
                    </Box>
                  </Box>
                </ColumnLayout>
              </SpaceBetween>
            </Container>

            {/* Historical Data Statistics */}
            <Container header={<Header variant="h3">Historische Datenstatistiken</Header>}>
              <ColumnLayout columns={4} borders="horizontal">
                <Box>
                  <Box variant="awsui-key-label">Durchschnittsrendite (μ)</Box>
                  <Box variant="awsui-value-large">{garchAnalysis.historicalMean}%</Box>
                  <Box variant="small" color="text-body-secondary">
                    Historischer Mittelwert p.a.
                  </Box>
                </Box>
                <Box>
                  <Box variant="awsui-key-label">Volatilität (σ)</Box>
                  <Box variant="awsui-value-large">{garchAnalysis.historicalVolatility}%</Box>
                  <Box variant="small" color="text-body-secondary">
                    Historische Standardabweichung p.a.
                  </Box>
                </Box>
                <Box>
                  <Box variant="awsui-key-label">Schiefe (Skewness)</Box>
                  <Box variant="awsui-value-large">{garchAnalysis.skewness}</Box>
                  <Box variant="small" color="text-body-secondary">
                    {parseFloat(garchAnalysis.skewness) < -0.1 ? 'Links-schief (Crash-Risiko)' :
                     parseFloat(garchAnalysis.skewness) > 0.1 ? 'Rechts-schief' : 'Symmetrisch'}
                  </Box>
                </Box>
                <Box>
                  <Box variant="awsui-key-label">Kurtosis (Wölbung)</Box>
                  <Box variant="awsui-value-large">{garchAnalysis.kurtosis}</Box>
                  <Box variant="small" color="text-body-secondary">
                    {parseFloat(garchAnalysis.kurtosis) > 3 ? 'Leptokurtisch (Fat Tails)' : 'Mesokurtisch'}
                  </Box>
                </Box>
              </ColumnLayout>
            </Container>

            {/* Technical Parameters Section */}
            <Container header={<Header variant="h3">EGARCH Parameter-Schätzungen</Header>}>
              <ColumnLayout columns={3} borders="horizontal">
                <Box>
                  <Box variant="h3">Grundparameter</Box>
                  <SpaceBetween direction="vertical" size="s">
                    <Box>
                      <Box variant="awsui-key-label">Konstante (ω)</Box>
                      <Box variant="awsui-value-large">
                        {/* Omega needs to be added to the analysis */}
                        Geschätzt
                      </Box>
                      <Box variant="small" color="text-body-secondary">
                        Langfristige log-Varianz Komponente
                      </Box>
                    </Box>
                    <Box>
                      <Box variant="awsui-key-label">Volatilitäts-Persistenz (β)</Box>
                      <Box variant="awsui-value-large">
                        {garchAnalysis.garchParameters.volatilityPersistence}
                      </Box>
                      <Box variant="small" color="text-body-secondary">
                        Langfristige Volatilitäts-Erhaltung
                      </Box>
                    </Box>
                  </SpaceBetween>
                </Box>
                <Box>
                  <Box variant="h3">ARCH/GARCH Effekte</Box>
                  <SpaceBetween direction="vertical" size="s">
                    <Box>
                      <Box variant="awsui-key-label">ARCH-Effekt (α)</Box>
                      <Box variant="awsui-value-large">
                        {garchAnalysis.garchParameters.archEffect}
                      </Box>
                      <Box variant="small" color="text-body-secondary">
                        Volatilitäts-Clustering Stärke
                      </Box>
                    </Box>
                    <Box>
                      <Box variant="awsui-key-label">Clustering-Effekt (γ)</Box>
                      <Box variant="awsui-value-large">
                        {garchAnalysis.garchParameters.clusteringEffect}
                      </Box>
                      <Box variant="small" color="text-body-secondary">
                        Magnituden-Effekt: große Schocks erhöhen Folge-Volatilität
                      </Box>
                    </Box>
                  </SpaceBetween>
                </Box>
                <Box>
                  <Box variant="h3">Asymmetrie</Box>
                  <SpaceBetween direction="vertical" size="s">
                    <Box>
                      <Box variant="awsui-key-label">Leverage-Effekt (θ)</Box>
                      <Box variant="awsui-value-large">
                        {garchAnalysis.garchParameters.leverageEffect}
                      </Box>
                      <Box variant="small" color="text-body-secondary">
                        Negative Schocks verstärken Volatilität
                      </Box>
                    </Box>
                    <Box>
                      <Box variant="awsui-key-label">Asymmetrie-Interpretation</Box>
                      <Badge color={parseFloat(garchAnalysis.garchParameters.leverageEffect) < 0 ? "green" : "severity-medium"}>
                        {parseFloat(garchAnalysis.garchParameters.leverageEffect) < 0 ? "Leverage-Effekt bestätigt" : "Asymmetrie schwach"}
                      </Badge>
                      <Box variant="small" color="text-body-secondary">
                        θ &lt; 0: Bad News erhöht Volatilität mehr
                      </Box>
                    </Box>
                  </SpaceBetween>
                </Box>
              </ColumnLayout>
            </Container>

            {/* Estimation Quality Section */}
            {garchAnalysis.estimationQuality && (
              <Container header={<Header variant="h3">Modell-Qualität & Schätzung</Header>}>
                <ColumnLayout columns={3} borders="horizontal">
                  <Box>
                    <Box variant="awsui-key-label">Schätzungsmethode</Box>
                    <Box variant="awsui-value-large">{garchAnalysis.estimationQuality.method}</Box>
                    <Box variant="small" color="text-body-secondary">
                      {garchAnalysis.estimationQuality.description}
                    </Box>
                  </Box>
                  <Box>
                    <Box variant="awsui-key-label">Modell-Qualität</Box>
                    <Badge color={garchAnalysis.estimationQuality.isFallback ? "severity-medium" : "green"}>
                      {garchAnalysis.estimationQuality.quality}
                    </Badge>
                    <Box variant="small" color="text-body-secondary">
                      {garchAnalysis.estimationQuality.isFallback ? 'Heuristisch stabil' : 'MLE-optimiert'}
                    </Box>
                  </Box>
                  <Box>
                    <Box variant="awsui-key-label">Modell-Güte</Box>
                    <Box variant="awsui-value-large">
                      {garchAnalysis.estimationQuality.logLikelihood || 'N/A'}
                    </Box>
                    <Box variant="small" color="text-body-secondary">
                      {garchAnalysis.estimationQuality.iterations ? 
                        `Log-Likelihood (${garchAnalysis.estimationQuality.iterations} Iterationen)` : 
                        'Log-Likelihood Wert'}
                    </Box>
                  </Box>
                </ColumnLayout>
              </Container>
            )}

            {/* Strategy-specific GARCH statistics */}
            {(threeTopfVolatilityStats || oneTopfVolatilityStats) && (
              <ColumnLayout columns={2} borders="vertical">
                {threeTopfVolatilityStats && (
                  <Box>
                    <Header variant="h3">3-Topf GARCH-Analyse</Header>
                    <SpaceBetween direction="vertical" size="xs">
                      <Box>
                        <Box variant="awsui-key-label">Durchschnittliche Volatilität</Box>
                        <Box variant="awsui-value-large">
                          {threeTopfVolatilityStats.meanVolatility.toFixed(1)}%
                        </Box>
                      </Box>
                      <Box>
                        <Box variant="awsui-key-label">95% Volatilitäts-Perzentil</Box>
                        <Box variant="awsui-value-large">
                          {threeTopfVolatilityStats.p95Volatility.toFixed(1)}%
                        </Box>
                      </Box>
                      <Box>
                        <Box variant="awsui-key-label">Volatilitäts-Clustering</Box>
                        <Badge color="red">
                          {threeTopfVolatilityStats.volatilityClusteringFrequency.toFixed(1)}%
                        </Badge>
                      </Box>
                      <Box>
                        <Box variant="awsui-key-label">Leverage-Effekt Häufigkeit</Box>
                        <Badge color="blue">
                          {threeTopfVolatilityStats.leverageEffectFrequency.toFixed(1)}%
                        </Badge>
                      </Box>
                    </SpaceBetween>
                  </Box>
                )}

                {oneTopfVolatilityStats && (
                  <Box>
                    <Header variant="h3">1-Topf GARCH-Analyse</Header>
                    <SpaceBetween direction="vertical" size="xs">
                      <Box>
                        <Box variant="awsui-key-label">Durchschnittliche Volatilität</Box>
                        <Box variant="awsui-value-large">
                          {oneTopfVolatilityStats.meanVolatility.toFixed(1)}%
                        </Box>
                      </Box>
                      <Box>
                        <Box variant="awsui-key-label">95% Volatilitäts-Perzentil</Box>
                        <Box variant="awsui-value-large">
                          {oneTopfVolatilityStats.p95Volatility.toFixed(1)}%
                        </Box>
                      </Box>
                      <Box>
                        <Box variant="awsui-key-label">Volatilitäts-Clustering</Box>
                        <Badge color="red">
                          {oneTopfVolatilityStats.volatilityClusteringFrequency.toFixed(1)}%
                        </Badge>
                      </Box>
                      <Box>
                        <Box variant="awsui-key-label">Leverage-Effekt Häufigkeit</Box>
                        <Badge color="blue">
                          {oneTopfVolatilityStats.leverageEffectFrequency.toFixed(1)}%
                        </Badge>
                      </Box>
                    </SpaceBetween>
                  </Box>
                )}
              </ColumnLayout>
            )}
          </SpaceBetween>
        </ExpandableSection>
      )}

      {/* Portfolio Charts - Side by Side */}
      <Container header={<Header variant="h3">Portfolio-Analyse</Header>}>
        <div style={{
          display: 'flex',
          gap: '16px',
          width: '100%',
          alignItems: 'stretch'
        }}>
          {/* Portfolio Distribution Chart */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <div style={{ marginBottom: '8px' }}>
              <Header variant="h3">
                Portfolio Endwert-Verteilung
              </Header>
            </div>
            <div style={{ flex: 1, minHeight: '320px' }}>
              <LineChart
                series={chartData}
                xDomain={[0, filteredMonteCarloResults.length]}
                yDomain={(() => {
                  if (!filteredMonteCarloResults || filteredMonteCarloResults.length === 0) {
                    return [0, 100];
                  }
                  const allBalances = filteredMonteCarloResults.flatMap(r => [
                    r.threeTopf.finalBalance,
                    r.oneTopf.finalBalance,
                  ]);
                  const maxBalance = Math.max(...allBalances);
                  return [0, maxBalance * 1.1]; // Add 10% padding
                })()}
                yScaleType="linear"
                xTitle="Iteration"
                yTitle="Endwert (€)"
                height={300}
                hideFilter={true}
                ariaLabel="Portfolio Endwert-Verteilung Chart"
                detailPopoverSeriesContent={({ series, y }) => ({
                  key: series.title,
                  value: `${Math.round(y)
                    .toString()
                    .replace(/\B(?=(\d{3})+(?!\d))/g, '.')} €`,
                })}
                empty={
                  <Box textAlign="center" color="inherit">
                    <b>Keine Daten</b>
                    <Box variant="p" color="inherit">
                      Es sind keine Monte Carlo Daten zum Anzeigen vorhanden.
                    </Box>
                  </Box>
                }
                loadingText="Lade Chart"
              />
            </div>
          </div>

          {/* Portfolio Survival Chart */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <div style={{ marginBottom: '8px' }}>
              <Header variant="h3">
                Portfolio Survival
              </Header>
            </div>
            <div style={{ flex: 1, minHeight: '320px' }}>
              <LineChart
                series={survivalChartData}
                xDomain={[
                  steuerParameter ? steuerParameter.retirementAge : 65,
                  steuerParameter ? steuerParameter.berechnungBisLebensalter : 100,
                ]}
                yDomain={[0, 100]}
                xTitle="Alter"
                yTitle="Portfolio Success (%)"
                height={300}
                hideFilter={true}
                ariaLabel="Portfolio Survival Chart"
                detailPopoverSeriesContent={({ series, y }) => ({
                  key: series.title,
                  value: `${Math.round(y)}%`,
                })}
                empty={
                  <Box textAlign="center" color="inherit">
                    <b>Keine Survival-Daten</b>
                    <Box variant="p" color="inherit">
                      Es sind keine Monte Carlo Daten zum Anzeigen vorhanden.
                    </Box>
                  </Box>
                }
                loadingText="Lade Chart"
              />
            </div>
          </div>
        </div>
      </Container>


      {/* Portfolio Balance Charts - Side by Side */}
      <Container header={<Header variant="h3">Portfolio - Gesamtwert über Zeit</Header>}>
        <div style={{
          display: 'flex',
          gap: '16px',
          width: '100%',
          alignItems: 'stretch'
        }}>
          {/* 3-Topf Portfolio Balance Chart */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <div style={{ marginBottom: '8px' }}>
              <Header variant="h3">
                3-Topf Portfolio - Gesamtwert über Zeit
              </Header>
            </div>
            <div style={{ flex: 1, minHeight: '380px' }}>
              <LineChart
                series={threeTopfBalanceChartData}
                xDomain={[
                  steuerParameter ? steuerParameter.retirementAge : 65,
                  steuerParameter ? steuerParameter.berechnungBisLebensalter : 100,
                ]}
                yDomain={(() => {
                  if (!threeTopfBalanceChartData || threeTopfBalanceChartData.length === 0) {
                    return [0, 1000000];
                  }
                  const allBalances = threeTopfBalanceChartData.flatMap(series =>
                    series.data.map(d => d.y)
                  );
                  const maxBalance = Math.max(...allBalances);
                  return [0, maxBalance * 1.1];
                })()}
                yScaleType="linear"
                xTitle="Alter"
                yTitle="Portfolio Balance (€)"
                height={360}
                hideFilter={true}
                ariaLabel="Portfolio Balance Chart"
                detailPopoverSeriesContent={({ series, y }) => ({
                  key: series.title,
                  value: `${Math.round(y)
                    .toString()
                    .replace(/\B(?=(\d{3})+(?!\d))/g, '.')} €`,
                })}
                empty={
                  <Box textAlign="center" color="inherit">
                    <b>Keine Balance-Daten</b>
                    <Box variant="p" color="inherit">
                      Es sind keine Portfolio-Balance-Daten zum Anzeigen vorhanden.
                    </Box>
                  </Box>
                }
                loadingText="Lade Chart"
              />
            </div>
          </div>

          {/* 1-Topf Portfolio Balance Chart */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <div style={{ marginBottom: '8px' }}>
              <Header variant="h3">
                1-Topf Portfolio - Gesamtwert über Zeit
              </Header>
            </div>
            <div style={{ flex: 1, minHeight: '380px' }}>
              <LineChart
                series={oneTopfBalanceChartData}
                xDomain={[
                  steuerParameter ? steuerParameter.retirementAge : 65,
                  steuerParameter ? steuerParameter.berechnungBisLebensalter : 100,
                ]}
                yDomain={(() => {
                  if (!oneTopfBalanceChartData || oneTopfBalanceChartData.length === 0) {
                    return [0, 1000000];
                  }
                  const allBalances = oneTopfBalanceChartData.flatMap(series =>
                    series.data.map(d => d.y)
                  );
                  const maxBalance = Math.max(...allBalances);
                  return [0, maxBalance * 1.1];
                })()}
                yScaleType="linear"
                xTitle="Alter"
                yTitle="Portfolio Balance (€)"
                height={360}
                hideFilter={true}
                ariaLabel="Portfolio Balance Chart"
                detailPopoverSeriesContent={({ series, y }) => ({
                  key: series.title,
                  value: `${Math.round(y)
                    .toString()
                    .replace(/\B(?=(\d{3})+(?!\d))/g, '.')} €`,
                })}
                empty={
                  <Box textAlign="center" color="inherit">
                    <b>Keine Balance-Daten</b>
                    <Box variant="p" color="inherit">
                      Es sind keine Portfolio-Balance-Daten zum Anzeigen vorhanden.
                    </Box>
                  </Box>
                }
                loadingText="Lade Chart"
              />
            </div>
          </div>
        </div>
      </Container>

      {/* 3-Topf Performance Analysis */}
      <Container
        header={
          <Header variant="h3">3-Topf Portfolio - Detaillierte Performance-Kennzahlen</Header>
        }
      >
        <Table
          columnDefinitions={[
            {
              id: 'metricWithInfo',
              header: 'Kennzahl',
              cell: (item: any) => renderMetricWithInfo(item.metricWithInfo),
              width: 250,
            },
            {
              id: 'p10',
              header: '10. Perzentil',
              cell: (item: any) => item.p10,
              width: 140,
            },
            {
              id: 'p25',
              header: '25. Perzentil',
              cell: (item: any) => item.p25,
              width: 140,
            },
            {
              id: 'p50',
              header: 'Median',
              cell: (item: any) => item.p50,
              width: 140,
            },
            {
              id: 'p75',
              header: '75. Perzentil',
              cell: (item: any) => item.p75,
              width: 140,
            },
            {
              id: 'p90',
              header: '90. Perzentil',
              cell: (item: any) => item.p90,
              width: 140,
            },
          ]}
          items={threeTopfPerformanceData}
          sortingDisabled={true}
          empty={
            <Box textAlign="center" color="inherit">
              <b>Keine Performance-Daten</b>
            </Box>
          }
        />
        <Box variant="small" color="text-body-secondary">
          {filteredMonteCarloResults.filter(r => r.threeTopf.successfulStrategy).length} Portfolios von{' '}
          {filteredMonteCarloResults.length} simulierten Portfolios (
          {(
            (filteredMonteCarloResults.filter(r => r.threeTopf.successfulStrategy).length /
              filteredMonteCarloResults.length) *
            100
          ).toFixed(2)}
          %) haben alle Entnahmen überlebt.
        </Box>
      </Container>

      {/* 1-Topf Performance Analysis */}
      <Container
        header={
          <Header variant="h3">1-Topf Portfolio - Detaillierte Performance-Kennzahlen</Header>
        }
      >
        <Table
          columnDefinitions={[
            {
              id: 'metricWithInfo',
              header: 'Kennzahl',
              cell: (item: any) => renderMetricWithInfo(item.metricWithInfo),
              width: 250,
            },
            {
              id: 'p10',
              header: '10. Perzentil',
              cell: (item: any) => item.p10,
              width: 140,
            },
            {
              id: 'p25',
              header: '25. Perzentil',
              cell: (item: any) => item.p25,
              width: 140,
            },
            {
              id: 'p50',
              header: 'Median',
              cell: (item: any) => item.p50,
              width: 140,
            },
            {
              id: 'p75',
              header: '75. Perzentil',
              cell: (item: any) => item.p75,
              width: 140,
            },
            {
              id: 'p90',
              header: '90. Perzentil',
              cell: (item: any) => item.p90,
              width: 140,
            },
          ]}
          items={oneTopfPerformanceData}
          sortingDisabled={true}
          empty={
            <Box textAlign="center" color="inherit">
              <b>Keine Performance-Daten</b>
            </Box>
          }
        />
        <Box variant="small" color="text-body-secondary">
          {filteredMonteCarloResults.filter(r => r.oneTopf.successfulStrategy).length} Portfolios von{' '}
          {filteredMonteCarloResults.length} simulierten Portfolios (
          {(
            (filteredMonteCarloResults.filter(r => r.oneTopf.successfulStrategy).length /
              filteredMonteCarloResults.length) *
            100
          ).toFixed(2)}
          %) haben alle Entnahmen überlebt.
        </Box>
      </Container>


      {/* Kapitalindex Charts - Side by Side */}
      <Container header={
        <Header
          variant="h3"
          description="Perzentile ab Depletion nur über verbleibende Läufe: sobald ein Teil der Simulationsläufe erschöpft ist, werden die Perzentile eines Jahres nur noch aus den überlebenden Läufen berechnet."
        >
          Portfolio - Kapitalindex Entwicklung
        </Header>
      }>
        <div style={{
          display: 'flex',
          gap: '16px',
          width: '100%',
          alignItems: 'stretch'
        }}>
          {/* 3-Topf Kapitalindex Chart */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <div style={{ marginBottom: '8px' }}>
              <Header variant="h3">
                3-Topf Portfolio - Kapitalindex Entwicklung
              </Header>
            </div>
            <div style={{ flex: 1, minHeight: '280px' }}>
              <LineChart
                series={threeTopfKapitalIndexChartData}
                xDomain={[
                  steuerParameter ? steuerParameter.retirementAge : 65,
                  steuerParameter ? steuerParameter.berechnungBisLebensalter : 100,
                ]}
                yDomain={[0, 500]}
                xTitle="Alter"
                yTitle="Kapitalindex"
                xScaleType="linear"
                statusType={filteredMonteCarloResults && filteredMonteCarloResults.length > 0 ? 'finished' : 'loading'}
                hideFilter={true}
                height={260}
                empty={
                  <Box textAlign="center" color="inherit">
                    <Box variant="strong" textAlign="center" color="inherit">
                      Keine Kapitalindex-Daten verfügbar
                    </Box>
                    <Box variant="p" color="inherit">
                      Es sind keine Kapitalindex-Daten zum Anzeigen vorhanden.
                    </Box>
                  </Box>
                }
                loadingText="Lade Kapitalindex Chart"
              />
            </div>
          </div>

          {/* 1-Topf Kapitalindex Chart */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <div style={{ marginBottom: '8px' }}>
              <Header variant="h3">
                1-Topf Portfolio - Kapitalindex Entwicklung
              </Header>
            </div>
            <div style={{ flex: 1, minHeight: '280px' }}>
              <LineChart
                series={oneTopfKapitalIndexChartData}
                xDomain={[
                  steuerParameter ? steuerParameter.retirementAge : 65,
                  steuerParameter ? steuerParameter.berechnungBisLebensalter : 100,
                ]}
                yDomain={[0, 500]}
                xTitle="Alter"
                yTitle="Kapitalindex"
                xScaleType="linear"
                statusType={filteredMonteCarloResults && filteredMonteCarloResults.length > 0 ? 'finished' : 'loading'}
                hideFilter={true}
                height={260}
                empty={
                  <Box textAlign="center" color="inherit">
                    <Box variant="strong" textAlign="center" color="inherit">
                      Keine Kapitalindex-Daten verfügbar
                    </Box>
                    <Box variant="p" color="inherit">
                      Es sind keine Kapitalindex-Daten zum Anzeigen vorhanden.
                    </Box>
                  </Box>
                }
                loadingText="Lade Kapitalindex Chart"
              />
            </div>
          </div>
        </div>
      </Container>

          </SpaceBetween>
        )
      },
        {
          label: 'Failure-Analyse',
          id: 'failure-analysis',
          content: monteCarloStats && displayMonteCarloStats && steuerParameter ? (
            <SpaceBetween direction="vertical" size="l">
              {/* Monte Carlo Analysis Filter */}
              <Container
                header={
                  <Header variant="h3">
                    Monte-Carlo-Analyse Einstellungen
                  </Header>
                }
              >
                <Toggle
                  checked={excludeExtremes}
                  onChange={({ detail }) => setExcludeExtremes(detail.checked)}
                >
                  <Box fontSize="body-m">
                    <strong>Extreme 2,5% ausschließen (beste & schlechteste)</strong>
                  </Box>
                </Toggle>
                {excludeExtremes && (
                  <Box color="text-status-info" fontSize="body-s" margin={{ top: 'xs' }}>
                    <Badge color="blue">Filter aktiv</Badge>
                    {' '}Zeige {filteredMonteCarloResults.length.toLocaleString('de-DE')} Iterationen
              (ausgeschlossen: {Math.floor(monteCarloResults.length * 0.025)} schlechteste + 
              {Math.ceil(monteCarloResults.length * 0.025)} beste = {monteCarloResults.length - filteredMonteCarloResults.length} extreme Szenarien)
                  </Box>
                )}
              </Container>

              {/* Critical Failure Analysis */}
              {monteCarloStats?.failureAnalysis && (
                <Container>
                  <Header 
                    variant="h2" 
                    description="Analyse der kritischen Szenarien, in denen die 3-Topf-Strategie früher versagt als die 1-Topf-Strategie"
                  >
                    Kritische Failure-Analyse
                  </Header>
                  
                  <SpaceBetween direction="vertical" size="m">
                    {/* Failure Statistics Overview */}
                    <ColumnLayout columns={3} variant="text-grid">
                      <div>
                        <Box variant="h4" color="text-status-error">
                          3-Topf Early Failures
                        </Box>
                        <Box fontSize="display-l" fontWeight="bold" color="text-status-error">
                          {displayMonteCarloStats?.failureAnalysis.threeTopfEarlyFailureRate.toFixed(1)}% ({Math.round(displayMonteCarloStats?.failureAnalysis.threeTopfEarlyFailureRate * displayMonteCarloStats?.iterations / 100)})
                        </Box>
                        <Box variant="small" color="text-body-secondary">
                          Anteil der Simulationen, in denen 3-Topf vor 1-Topf versagte
                        </Box>
                      </div>
                      
                      <div>
                        <Box variant="h4" color="text-status-warning">
                          1-Topf Early Failures  
                        </Box>
                        <Box fontSize="display-l" fontWeight="bold" color="text-status-warning">
                          {displayMonteCarloStats?.failureAnalysis.oneTopfEarlyFailureRate.toFixed(1)}% ({Math.round(displayMonteCarloStats?.failureAnalysis.oneTopfEarlyFailureRate * displayMonteCarloStats?.iterations / 100)})
                        </Box>
                        <Box variant="small" color="text-body-secondary">
                          Anteil der Simulationen, in denen 1-Topf vor 3-Topf versagte
                        </Box>
                      </div>
                      
                      <div>
                        <Box variant="h4" color="text-status-info">
                          Beide Strategien versagten
                        </Box>
                        <Box fontSize="display-l" fontWeight="bold" color="text-status-info">
                          {displayMonteCarloStats?.failureAnalysis.bothFailedRate.toFixed(1)}% ({Math.round(displayMonteCarloStats?.failureAnalysis.bothFailedRate * displayMonteCarloStats?.iterations / 100)})
                        </Box>
                        <Box variant="small" color="text-body-secondary">
                          Anteil der Simulationen mit kompletter Kapitalerschöpfung
                        </Box>
                      </div>
                    </ColumnLayout>

                    {/* Total Failure Rates */}
                    <ColumnLayout columns={2} variant="text-grid">
                      <div>
                        <Box variant="h4" color="text-status-error">
                          3-Topf Failures (vorzeitige Depletions)
                        </Box>
                        <Box fontSize="display-l" fontWeight="bold" color="text-status-error">
                          {((depletionCounts.threeTopf.early / filteredMonteCarloResults.length) * 100).toFixed(1)}% ({depletionCounts.threeTopf.early})
                        </Box>
                        <Box variant="small" color="text-body-secondary">
                          Portfolios, die vor Zielalter erschöpft wurden
                        </Box>
                      </div>
                      
                      <div>
                        <Box variant="h4" color="text-status-warning">
                          1-Topf Failures (vorzeitige Depletions)
                        </Box>
                        <Box fontSize="display-l" fontWeight="bold" color="text-status-warning">
                          {((depletionCounts.oneTopf.early / filteredMonteCarloResults.length) * 100).toFixed(1)}% ({depletionCounts.oneTopf.early})
                        </Box>
                        <Box variant="small" color="text-body-secondary">
                          Portfolios, die vor Zielalter erschöpft wurden
                        </Box>
                      </div>
                    </ColumnLayout>

                    {/* Critical Scenarios Alert */}
                    {(displayMonteCarloStats?.failureAnalysis?.threeTopfEarlyFailureRate ?? 0) > 10 && (
                      <Alert 
                        type="warning"
                        header="Kritische 3-Topf-Failure-Rate erkannt"
                      >
                        In {displayMonteCarloStats?.failureAnalysis.threeTopfEarlyFailureRate.toFixed(1)}% der Simulationen 
                        versagte die 3-Topf-Strategie früher als die 1-Topf-Strategie. Dies deutet auf systematische 
                        Schwächen in bestimmten Marktszenarien hin. Prüfen Sie die Worst-Case-Szenarien unten für Details.
                      </Alert>
                    )}

                  </SpaceBetween>
                </Container>
              )}
              
              {displayMonteCarloStats && (
                <FailureAnalysisPanel
                  monteCarloResults={filteredMonteCarloResults}
                  monteCarloStats={displayMonteCarloStats}
                  steuerParameter={steuerParameter}
                  monteCarloEngine={monteCarloEngine}
                  crisisDefinition={crisisDefinition as 'negative-return' | 'kapitalindex-drawdown'}
                />
              )}
            </SpaceBetween>
          ) : (
            <Box textAlign="center" color="text-body-secondary">
              <p>Keine Daten verfügbar für Failure-Analyse</p>
            </Box>
          )
        },
        {
          label: 'Zinstopf-Optimierung',
          id: 'zinstopf-optimization',
          content: (
            <ZinstopfOptimizationTab
              steuerParameter={steuerParameter}
              monteCarloEngine={monteCarloEngine}
              iterations={steuerParameter?.monteCarloIterations || 1000}
            />
          )
        },
        {
          label: 'Entnahme-Rate Optimierung',
          id: 'withdrawal-rate-optimization',
          content: (
            <WithdrawalRateOptimizationTab
              steuerParameter={steuerParameter}
              monteCarloEngine={monteCarloEngine}
              iterations={steuerParameter?.monteCarloIterations || 1000}
            />
          )
        },
        {
          label: 'Zinstopf-Entnahme-Optimierung',
          id: 'combined-optimization',
          content: (
            <CombinedOptimizationTab
              steuerParameter={steuerParameter}
              monteCarloEngine={monteCarloEngine}
              iterations={steuerParameter?.monteCarloIterations || 1000}
            />
          )
        },
        {
          label: 'Wann gewinnt der 3-Topf?',
          id: 'scenario-matrix',
          content: (
            <ScenarioMatrixTab
              steuerParameter={steuerParameter}
              monteCarloEngine={monteCarloEngine}
              iterations={steuerParameter?.monteCarloIterations || 1000}
              simulationOptions={simulationOptions}
            />
          )
        }
      ]}
    />
    </>
  );
};
